/**
 * RLE (Run-Length Encoding) utilities for decoding segmentation masks.
 *
 * Matches iOS implementation: Glean/DataModels/Annotations.swift lines 272-358
 *
 * The iOS app encodes masks using:
 * 1. Column-major (Fortran) ordering for COCO compatibility
 * 2. Run-length encoding: [count0, count1, count0, count1, ...]
 * 3. LEB128 variable-length integer compression
 * 4. Base64 encoding for JSON storage
 *
 * This file provides utilities to decode back to COCO-compatible RLE format.
 */

import type { CompressedRLEMask } from "@/types/annotations";

/**
 * Yield to the main thread to allow UI updates
 */
function yieldToMain(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * COCO RLE format (uncompressed)
 * https://github.com/cocodataset/cocoapi/blob/master/PythonAPI/pycocotools/mask.py
 */
export interface CocoRLE {
  counts: number[];         // Run-length counts [count0, count1, count0, count1, ...]
  size: [number, number];   // [height, width]
}

/**
 * COCO RLE format (compressed - for Roboflow compatibility)
 * Uses byte string encoding compatible with pycocotools
 */
export interface CocoRLECompressed {
  counts: string;           // LEB128-compressed and base64-encoded byte string
  size: [number, number];   // [height, width]
}

/**
 * Decode LEB128-compressed integer from byte array.
 *
 * LEB128 (Little Endian Base 128) is a variable-length integer encoding:
 * - Each byte stores 7 bits of value and 1 continuation bit
 * - Continuation bit (bit 7) = 1 means more bytes follow
 * - Continuation bit = 0 means this is the last byte
 *
 * Matches iOS: Glean/DataModels/Annotations.swift lines 293-310
 *
 * @param bytes - Byte array to decode from
 * @param offset - Starting offset (updated to next position)
 * @returns Decoded integer, or null if invalid
 */
function decodeLEB128(bytes: Uint8Array, offset: { value: number }): number | null {
  let result = 0;
  let shift = 0;

  while (offset.value < bytes.length) {
    const byte = bytes[offset.value++];
    result |= (byte & 0x7F) << shift;

    if ((byte & 0x80) === 0) {
      // No continuation bit - we're done
      return result;
    }

    shift += 7;

    if (shift > 63) {
      // Overflow protection (64-bit limit)
      return null;
    }
  }

  // Reached end without finding terminator
  return null;
}

/**
 * Decompress LEB128-encoded data to array of integers.
 *
 * Matches iOS: Glean/DataModels/Annotations.swift lines 328-342
 *
 * @param data - LEB128-compressed byte array
 * @returns Array of decompressed integers, or null if invalid
 */
export function decompressLEB128(data: Uint8Array): number[] | null {
  const counts: number[] = [];
  const offset = { value: 0 };

  while (offset.value < data.length) {
    const count = decodeLEB128(data, offset);
    if (count === null) {
      return null;
    }
    counts.push(count);
  }

  return counts;
}

/**
 * Encode integer to LEB128 format.
 *
 * Reverse of decodeLEB128 - encodes a single integer to variable-length bytes.
 *
 * @param value - Integer to encode
 * @returns Array of bytes representing the LEB128-encoded value
 */
function encodeLEB128(value: number): number[] {
  const bytes: number[] = [];

  do {
    let byte = value & 0x7F; // Take lowest 7 bits
    value >>>= 7; // Unsigned right shift by 7

    if (value !== 0) {
      byte |= 0x80; // Set continuation bit if more bytes follow
    }

    bytes.push(byte);
  } while (value !== 0);

  return bytes;
}

/**
 * Compress array of integers to LEB128-encoded byte array.
 *
 * Reverse of decompressLEB128 - encodes an array of integers to compressed format.
 *
 * @param counts - Array of integers to compress
 * @returns Uint8Array of LEB128-compressed data
 */
export function compressLEB128(counts: number[]): Uint8Array {
  const allBytes: number[] = [];

  for (const count of counts) {
    const bytes = encodeLEB128(count);
    allBytes.push(...bytes);
  }

  return new Uint8Array(allBytes);
}

/**
 * Decode CompressedRLEMask to COCO RLE format.
 *
 * Converts from iOS app's compressed format to COCO-compatible uncompressed RLE.
 *
 * The iOS format is:
 * ```
 * {
 *   "compressedCounts": "base64String",  // LEB128-compressed counts
 *   "size": [height, width]
 * }
 * ```
 *
 * COCO format is:
 * ```
 * {
 *   "counts": [count0, count1, count0, count1, ...],  // Uncompressed
 *   "size": [height, width]
 * }
 * ```
 *
 * @param mask - CompressedRLEMask from iOS app
 * @returns COCO RLE format, or null if decoding fails
 */
export function decodeCompressedRLE(mask: CompressedRLEMask): CocoRLE | null {
  try {
    // Step 1: Base64 decode
    const base64String = mask.compressedCounts;
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Step 2: LEB128 decompress
    const counts = decompressLEB128(bytes);
    if (!counts) {
      console.error("Failed to decompress LEB128 data");
      return null;
    }

    // Step 3: Return COCO format
    return {
      counts,
      size: [mask.size[0], mask.size[1]],
    };
  } catch (error) {
    console.error("Error decoding compressed RLE:", error);
    return null;
  }
}

/**
 * Decode RLE counts to 2D binary mask.
 *
 * RLE format uses column-major (Fortran) ordering:
 * - Iterate through columns first, then rows
 * - Counts alternate: [bg, fg, bg, fg, ...]
 *
 * @param counts - Run-length counts
 * @param height - Mask height
 * @param width - Mask width
 * @returns 2D boolean array [row][col], or null if invalid
 */
export function rleToMask(counts: number[], height: number, width: number): boolean[][] | null {
  const totalPixels = height * width;
  const flat: boolean[] = new Array(totalPixels);

  // Decode runs to flat array
  let pixelIndex = 0;
  let isBackground = true; // First count is always background (0)

  for (const count of counts) {
    const value = !isBackground; // true for foreground, false for background
    for (let i = 0; i < count; i++) {
      if (pixelIndex >= totalPixels) {
        console.error("RLE counts exceed image size");
        return null;
      }
      flat[pixelIndex++] = value;
    }
    isBackground = !isBackground;
  }

  // Fill remaining pixels with background if needed
  while (pixelIndex < totalPixels) {
    flat[pixelIndex++] = false;
  }

  // Convert flat array to 2D using column-major ordering
  const mask: boolean[][] = Array.from({ length: height }, () =>
    new Array(width).fill(false)
  );

  for (let col = 0; col < width; col++) {
    for (let row = 0; row < height; row++) {
      const flatIndex = col * height + row; // Column-major
      mask[row][col] = flat[flatIndex];
    }
  }

  return mask;
}

/**
 * Encode 2D binary mask to RLE counts.
 *
 * Uses column-major (Fortran) ordering for COCO compatibility.
 * Yields periodically to keep UI responsive.
 *
 * @param mask - 2D boolean array [row][col]
 * @param shouldCancel - Optional callback to check if operation should be cancelled
 * @returns Run-length counts array
 */
async function maskToRLE(mask: boolean[][], shouldCancel?: () => boolean): Promise<number[]> {
  const height = mask.length;
  const width = mask[0]?.length || 0;

  // Flatten to 1D using column-major ordering (yield every 500 columns)
  const flat: boolean[] = new Array(height * width);
  for (let col = 0; col < width; col++) {
    for (let row = 0; row < height; row++) {
      const flatIndex = col * height + row; // Column-major
      flat[flatIndex] = mask[row][col];
    }
    // Yield every 500 columns to keep UI responsive
    if (col > 0 && col % 500 === 0) {
      await yieldToMain();
      // Check for cancellation
      if (shouldCancel?.()) {
        throw new Error("Download cancelled");
      }
    }
  }

  // Encode runs
  const counts: number[] = [];
  let currentValue = false; // Always start with background
  let currentCount = 0;

  for (const pixel of flat) {
    if (pixel === currentValue) {
      currentCount++;
    } else {
      counts.push(currentCount);
      currentValue = pixel;
      currentCount = 1;
    }
  }

  // Push final count
  if (currentCount > 0) {
    counts.push(currentCount);
  }

  return counts;
}

/**
 * Resize binary mask using nearest-neighbor interpolation.
 *
 * Proper nearest-neighbor samples from pixel centers to avoid off-by-one errors.
 * Formula: oldCoord = floor((newCoord + 0.5) * oldSize / newSize)
 *
 * @param mask - Input binary mask [row][col]
 * @param newHeight - Target height
 * @param newWidth - Target width
 * @returns Resized binary mask
 */
function resizeMask(mask: boolean[][], newHeight: number, newWidth: number): boolean[][] {
  const oldHeight = mask.length;
  const oldWidth = mask[0]?.length || 0;

  const resized: boolean[][] = Array.from({ length: newHeight }, () =>
    new Array(newWidth).fill(false)
  );

  // Nearest-neighbor interpolation with proper pixel center sampling
  for (let newRow = 0; newRow < newHeight; newRow++) {
    for (let newCol = 0; newCol < newWidth; newCol++) {
      // Map new coordinates back to old coordinates (sample from pixel centers)
      const oldRow = Math.floor((newRow + 0.5) * oldHeight / newHeight);
      const oldCol = Math.floor((newCol + 0.5) * oldWidth / newWidth);

      // Clamp to bounds
      const clampedRow = Math.min(Math.max(0, oldRow), oldHeight - 1);
      const clampedCol = Math.min(Math.max(0, oldCol), oldWidth - 1);

      resized[newRow][newCol] = mask[clampedRow][clampedCol];
    }
  }

  return resized;
}

/**
 * Expand bbox-cropped RLE mask to full image size.
 *
 * iOS app stores masks cropped AND downsampled for efficiency.
 * COCO format expects full-image-sized masks.
 *
 * This function:
 * 1. Decodes cropped RLE to 2D binary mask
 * 2. Scales mask to bbox dimensions (iOS downsamples by ~5x)
 * 3. Creates full-image zero mask
 * 4. Pastes scaled mask at bbox position
 * 5. Re-encodes to full-image RLE
 *
 * @param croppedMask - Bbox-cropped CompressedRLEMask from iOS
 * @param bbox - Bounding box in pixels [x, y, width, height]
 * @param imageWidth - Full image width
 * @param imageHeight - Full image height
 * @param compressed - If true, return compressed string format for Roboflow (default: true)
 * @returns Full-image-sized COCO RLE, or null if expansion fails
 */
export async function expandBBoxRLEToFullImage(
  croppedMask: CompressedRLEMask,
  bbox: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
  compressed: boolean = true,
  shouldCancel?: () => boolean
): Promise<CocoRLE | CocoRLECompressed | null> {
  // Step 1: Decode cropped RLE
  const croppedRLE = decodeCompressedRLE(croppedMask);
  if (!croppedRLE) {
    return null;
  }

  // Step 2: Decode to 2D binary mask
  const [croppedHeight, croppedWidth] = croppedRLE.size;
  const croppedBinaryMask = rleToMask(croppedRLE.counts, croppedHeight, croppedWidth);
  if (!croppedBinaryMask) {
    return null;
  }

  // Step 3: Scale mask to bbox dimensions (iOS downsamples for storage)
  const bboxWidth = Math.round(bbox.width);
  const bboxHeight = Math.round(bbox.height);
  const scaledMask = resizeMask(croppedBinaryMask, bboxHeight, bboxWidth);

  // Step 4: Create full-image zero mask
  const fullMask: boolean[][] = Array.from({ length: imageHeight }, () =>
    new Array(imageWidth).fill(false)
  );

  // Step 5: Paste scaled mask at bbox position (yield periodically)
  const bboxX = Math.round(bbox.x);
  const bboxY = Math.round(bbox.y);

  for (let row = 0; row < bboxHeight; row++) {
    for (let col = 0; col < bboxWidth; col++) {
      const fullRow = bboxY + row;
      const fullCol = bboxX + col;

      if (fullRow >= 0 && fullRow < imageHeight && fullCol >= 0 && fullCol < imageWidth) {
        fullMask[fullRow][fullCol] = scaledMask[row][col];
      }
    }
    if (row > 0 && row % 500 === 0) {
      await yieldToMain();
      if (shouldCancel?.()) {
        throw new Error("Download cancelled");
      }
    }
  }

  // Step 6: Re-encode to full-image RLE
  const fullCounts = await maskToRLE(fullMask, shouldCancel);

  const uncompressedRLE: CocoRLE = {
    counts: fullCounts,
    size: [imageHeight, imageWidth],
  };

  if (compressed) {
    return compressCocoRLE(uncompressedRLE);
  }
  return uncompressedRLE;
}

/**
 * Convert CompressedRLEMask to COCO annotation format.
 *
 * COCO expects either:
 * 1. Polygon: `"segmentation": [[x1, y1, x2, y2, ...]]`
 * 2. RLE: `"segmentation": {"counts": [...], "size": [h, w]}`
 *
 * This function converts iOS CompressedRLEMask to COCO RLE format.
 *
 * @param mask - CompressedRLEMask from iOS app
 * @returns COCO RLE object, or null if decoding fails
 */
export function convertToCocoRLE(mask: CompressedRLEMask): CocoRLE | null {
  return decodeCompressedRLE(mask);
}

/**
 * Compress RLE counts to COCO compressed string format.
 *
 * Converts uncompressed RLE counts array to compressed base64 string
 * format compatible with pycocotools and Roboflow.
 *
 * @param rle - Uncompressed COCO RLE with counts array
 * @returns Compressed COCO RLE with base64-encoded string
 */
export function compressCocoRLE(rle: CocoRLE): CocoRLECompressed {
  // Compress counts using LEB128
  const compressed = compressLEB128(rle.counts);

  // Convert to base64 string
  const base64 = btoa(String.fromCharCode(...compressed));

  return {
    counts: base64,
    size: rle.size
  };
}

/**
 * Decompress COCO compressed RLE string to uncompressed counts array.
 *
 * Reverse of compressCocoRLE - for testing/validation.
 *
 * @param compressed - Compressed COCO RLE with base64 string
 * @returns Uncompressed COCO RLE with counts array, or null if invalid
 */
export function decompressCocoRLE(compressed: CocoRLECompressed): CocoRLE | null {
  try {
    // Decode base64 to bytes
    const binaryString = atob(compressed.counts);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decompress LEB128 to counts
    const counts = decompressLEB128(bytes);
    if (!counts) {
      return null;
    }

    return {
      counts,
      size: compressed.size
    };
  } catch (error) {
    console.error('Failed to decompress COCO RLE:', error);
    return null;
  }
}

/**
 * Decode CompressedRLEMask directly to 2D boolean mask.
 *
 * Convenience function that combines decodeCompressedRLE and rleToMask.
 * Returns the mask at its native (cropped) size - caller should scale/position as needed.
 *
 * @param mask - CompressedRLEMask from iOS app (bbox-cropped)
 * @returns 2D boolean array [row][col] at cropped size, or null if decoding fails
 */
export function decodeCompressedRLEToMask(mask: CompressedRLEMask): boolean[][] | null {
  const rle = decodeCompressedRLE(mask);
  if (!rle) {
    return null;
  }

  const [height, width] = rle.size;
  return rleToMask(rle.counts, height, width);
}
